import { useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import type { EmployeeRow } from '../employeeTypes';
import type {
  FilterState,
  SortState,
  TableColumn,
} from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { EmployeeApiFactory } from '../api/employeeApi';
import type { Response } from '../api/employeeApi';
import { DEFAULT_PAGE, DEFAULT_LIMIT } from '../../../hooks/usePaginationState';

export { DEFAULT_PAGE, DEFAULT_LIMIT };

interface UseGetEmployeesResult {
  data: EmployeeRow[];
  loading: boolean;
  error: ApiError | Error | null;
  meta: Response<EmployeeRow>['meta'] | null;
}

const EMPTY_FILTER_STATE: FilterState<EmployeeRow> = {};

export function useGetEmployees(
  columns: TableColumn<EmployeeRow>[], //dictionnaire de mapping pour sérialiser correctement ces filtres
  sortState: SortState<EmployeeRow> | null,
  filterState: FilterState<EmployeeRow> = EMPTY_FILTER_STATE,
  page: number = DEFAULT_PAGE,
  limit: number = DEFAULT_LIMIT,
): UseGetEmployeesResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => EmployeeApiFactory(client), [client]);

  const [data, setData] = useState<EmployeeRow[]>([]);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [meta, setMeta] = useState<Response<EmployeeRow>['meta'] | null>(null);

  // Cancel the fetch if the component unmounts or sort/filter/page changes
  useEffect(() => {
    const ctrl = new AbortController();
    api
      .getAll(
        {
          sortBy: sortState?.sortBy != null ? String(sortState.sortBy) : undefined,
          sortOrder: sortState?.sortOrder,
          filters: filterState,
          columns,
          page,
          limit,
        },
        { signal: ctrl.signal }, //permet d'annuler la requête au unmount/changement (AbortController)
      )
      .then(response => {
        setError(null);
        setData(response.data);
        setMeta(response.meta);
      })
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => ctrl.abort();
  }, [api, columns, sortState, filterState, page, limit]);

  return { data, loading, error, meta };
}
